import React from 'react';
import { clsx } from 'clsx';
import { OceanCreditLogoWhite } from '@/assets/logos';
import { Icon } from '@/ui/icons/Icon';
import { formatAmountWithSpaces } from '@/lib/amountUtils';
import { formatExpiryCountdown } from '@/lib/timeUtils';
import { useOfferExpiry } from '../../hooks';
import { BrokerProductType } from '../../types/brokerOffer';
import type { BrokerOfferDetails } from '../../types/brokerOffer';
import styles from './BrokerOfferCard.module.scss';

export interface BrokerOfferCardProps {
  offer: BrokerOfferDetails;
  className?: string;
}

export const BrokerOfferCard: React.FC<BrokerOfferCardProps> = ({
  offer,
  className,
}) => {
  const { secondsLeft, isExpired } = useOfferExpiry(offer);

  const isCreditLine = offer.productType === BrokerProductType.CreditLine;
  const monthlyPayment = offer.rates?.[0]?.amount;

  return (
    <div className={clsx(styles.root, className)}>
      <div
        className={clsx(
          styles.card,
          isCreditLine ? styles['card--creditLine'] : styles['card--consume']
        )}
      >
        <div className={styles.cardOverlay} />

        <div className={styles.cardHeader}>
          <OceanCreditLogoWhite className={styles.logo} />
          <Icon
            name={isCreditLine ? 'credit-card' : 'coins'}
            size={36}
            className={styles.headerIcon}
          />
        </div>

        <div className={styles.cardBottom}>
          <div className={styles.amountGroup}>
            <p className={styles.cardLabel}>
              {isCreditLine ? 'Linie de credit' : 'Credit în rate'}
            </p>
            <div className={styles.amountValue}>
              <span className={styles.amount}>
                {formatAmountWithSpaces(offer.amount)}
              </span>
              <span className={styles.amountCurrency}>lei</span>
            </div>
          </div>

          {!isCreditLine && (
            <div className={styles.monthlyGroup}>
              <p className={styles.cardLabel}>Rata lunară</p>
              <div className={styles.monthlyValue}>
                <span className={styles.monthly}>
                  {formatAmountWithSpaces(monthlyPayment)}
                </span>
                <span className={styles.monthlyCurrency}>lei</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={clsx(
          styles.brokerRow,
          isExpired && styles['brokerRow--expired']
        )}
      >
        <div className={styles.brokerCol}>
          <p className={styles.metaLabel}>Oferta obținută prin</p>
          <p className={styles.metaValue}>{offer.brokerName}</p>
        </div>
        <div className={styles.expiryCol}>
          {isExpired ? (
            <p className={styles.expiredText}>Ofertă expirată</p>
          ) : (
            <>
              <p className={styles.metaLabel}>Expiră în</p>
              <p className={styles.expiryValue}>
                {formatExpiryCountdown(secondsLeft)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
